/**
 * Engine-neutral text-layout helpers: word-aware wrapping, mid-word break,
 * and "…" truncation.
 *
 * Caching is delegated to the per-engine `TextMetricsCache` passed in
 * (optional). When provided, wrap/truncate results land in
 * `textMetrics.wrappedText` / `textMetrics.truncatedLabel`, and individual
 * `ctx.measureText` calls route through `textMetrics.measureText`. When
 * omitted (e.g., in tests), everything computes fresh against the raw
 * canvas context. No module-level caches live here — they'd be the same
 * "module singleton" pattern we deliberately moved off for measurements.
 */

import type { TextMetricsCache } from "@/components/ecv2/engine/canvasEngine/textMetrics";

// ============================================================================
// Types
// ============================================================================

export type WrappedTextResult = {
  lines: string[];
  height: number;
};

// ============================================================================
// Internal helpers
// ============================================================================

/**
 * Measure a single string's width via the per-engine `TextMetricsCache`
 * when provided, falling back to raw `ctx.measureText` otherwise.
 */
function measureWidth(
  ctx: CanvasRenderingContext2D,
  text: string,
  textMetrics?: TextMetricsCache
): number {
  if (textMetrics) {
    return textMetrics.measureText(ctx, text, ctx.font);
  }
  return ctx.measureText(text).width;
}

// ============================================================================
// Truncation
// ============================================================================

/**
 * Truncate `label` to fit within `maxWidth`, appending "..." if truncated.
 * Engine-neutral: returns the input unchanged when it already fits.
 */
export function truncateLabel(
  label: string,
  maxWidth: number,
  ctx: CanvasRenderingContext2D,
  textMetrics?: TextMetricsCache
): string {
  if (measureWidth(ctx, label, textMetrics) <= maxWidth) return label;

  let truncated = label;
  while (truncated.length > 0) {
    truncated = truncated.slice(0, -1);
    if (measureWidth(ctx, `${truncated}...`, textMetrics) <= maxWidth) return `${truncated}...`;
  }
  return "...";
}

/**
 * Get truncated label from the per-engine cache, or compute and store it.
 * Callers that already have a cache key should use this over `truncateLabel`.
 */
export function getCachedTruncatedLabel(
  ctx: CanvasRenderingContext2D,
  label: string,
  maxWidth: number,
  font: string,
  textMetrics: TextMetricsCache
): string {
  const cacheKey = `${label}|${maxWidth}|${font}`;
  const cached = textMetrics.truncatedLabel.get(cacheKey);
  if (cached !== undefined) return cached;

  const result = truncateLabel(label, maxWidth, ctx, textMetrics);
  textMetrics.truncatedLabel.set(cacheKey, result);
  return result;
}

/**
 * Truncate `line` to fit within `maxWidth`, always appending "…" — even when
 * the line fits. Use when more content exists below the visible area and the
 * caller wants to signal that there's more.
 */
export function truncateLineWithEllipsis(
  ctx: CanvasRenderingContext2D,
  line: string,
  maxWidth: number,
  textMetrics?: TextMetricsCache
): string {
  const ellipsis = "\u2026";
  const ellipsisWidth = measureWidth(ctx, ellipsis, textMetrics);
  if (maxWidth <= ellipsisWidth) {
    return ellipsis;
  }
  const targetWidth = maxWidth - ellipsisWidth;
  let truncated = line;
  while (truncated.length > 0) {
    if (measureWidth(ctx, truncated, textMetrics) <= targetWidth) {
      return truncated + ellipsis;
    }
    truncated = truncated.slice(0, -1);
  }
  return ellipsis;
}

// ============================================================================
// Word wrapping
// ============================================================================

/**
 * Measure `text` with word-aware wrapping:
 *  - Break at word boundaries (spaces) first.
 *  - Break mid-word only if a single word exceeds `maxWidth`.
 *
 * When `textMetrics` is provided, the result lands in
 * `textMetrics.wrappedText` keyed by (text, maxWidth, font, lineHeight).
 */
export function measureWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  font: string,
  lineHeight: number,
  textMetrics?: TextMetricsCache
): WrappedTextResult {
  if (!textMetrics) {
    return computeWrappedText(ctx, text, maxWidth, lineHeight);
  }

  const cacheKey = `wrap|${text}|${maxWidth}|${font}|${lineHeight}`;
  const cached = textMetrics.wrappedText.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const result = computeWrappedText(ctx, text, maxWidth, lineHeight, textMetrics);
  textMetrics.wrappedText.set(cacheKey, result);
  return result;
}

function computeWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  lineHeight: number,
  textMetrics?: TextMetricsCache
): WrappedTextResult {
  if (!text || maxWidth <= 0) {
    return { lines: [], height: 0 };
  }

  const lines = wrapTextLines(text, maxWidth, (candidate) =>
    measureWidth(ctx, candidate, textMetrics)
  );

  return {
    lines,
    height: lines.length * lineHeight,
  };
}

/**
 * Engine-neutral core of word-aware wrapping: splits `text` on whitespace,
 * greedily packs words per line via `measureWidth`, and mid-word breaks a
 * single word that alone exceeds `maxWidth`. Takes a plain width-measuring
 * function rather than a canvas context so callers that need a different
 * measurement source (e.g. a stubbed measurer in tests) can reuse the same
 * wrapping algorithm without a `CanvasRenderingContext2D`.
 */
export function wrapTextLines(
  text: string,
  maxWidth: number,
  measureWidth: (candidate: string) => number,
  splitWords: (text: string) => string[] = (t) => t.split(/\s+/)
): string[] {
  if (measureWidth(text) <= maxWidth) {
    return [text];
  }

  const lines: string[] = [];
  const words = splitWords(text);
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = measureWidth(testLine);

    if (testWidth <= maxWidth) {
      currentLine = testLine;
    } else if (currentLine) {
      lines.push(currentLine);
      if (measureWidth(word) <= maxWidth) {
        currentLine = word;
      } else {
        const brokenLines = breakWord(word, maxWidth, measureWidth);
        lines.push(...brokenLines.slice(0, -1));
        currentLine = brokenLines[brokenLines.length - 1] ?? "";
      }
    } else {
      const brokenLines = breakWord(word, maxWidth, measureWidth);
      lines.push(...brokenLines.slice(0, -1));
      currentLine = brokenLines[brokenLines.length - 1] ?? "";
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

function breakWord(
  word: string,
  maxWidth: number,
  measureWidth: (candidate: string) => number
): string[] {
  const lines: string[] = [];
  let remaining = word;

  while (remaining) {
    let fit = "";
    for (let i = 1; i <= remaining.length; i++) {
      const test = remaining.slice(0, i);
      if (measureWidth(test) > maxWidth) {
        break;
      }
      fit = test;
    }
    if (!fit) {
      fit = remaining.slice(0, 1);
    }
    lines.push(fit);
    remaining = remaining.slice(fit.length);
  }

  return lines;
}
